import React, { useState, useRef } from 'react';
import { X } from 'lucide-react';

const PostMediaGrid = ({ post }) => {
  const [carouselIndex, setCarouselIndex] = useState(null);
  const touchStartRef = useRef(null);

  // Collect all media items into a single flat array
  const allMedia = [];
  if (post.video?.url) {
    allMedia.push({ type: 'video', src: post.video.url, filename: post.video.filename, thumbnail: post.video.thumbnail || null });
  } else if (typeof post.video === 'string' && post.video) {
    allMedia.push({ type: 'video', src: post.video });
  }
  // Check imagePreviews, images, and media fields
  const imageList = post.imagePreviews || post.images || post.media || [];
  imageList.forEach((src) => {
    const url = typeof src === 'string' ? src : src?.url;
    if (url) allMedia.push({ type: 'image', src: url });
  });

  if (allMedia.length === 0) return null;

  const thumbSize = allMedia.length === 1 ? '120px' : '80px';
  const gridCols = allMedia.length === 1 ? 1 : allMedia.length === 2 ? 2 : Math.min(allMedia.length, 4);

  return (
    <div className="mb-3">
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {allMedia.map((item, i) => (
          <div
            key={i}
            style={{ width: thumbSize, height: thumbSize, borderRadius: '8px', overflow: 'hidden', cursor: 'pointer', position: 'relative', flexShrink: 0, backgroundColor: '#1f2937' }}
            onClick={() => setCarouselIndex(i)}
          >
            {item.type === 'image' ? (
              <img
                src={item.src}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <>
                {item.thumbnail ? (
                  <img
                    src={item.thumbnail}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <video
                    src={item.src}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    muted
                    playsInline
                    preload="auto"
                    onLoadedData={(e) => { e.target.currentTime = 0.5; }}
                  />
                )}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: '32px', height: '32px', backgroundColor: 'rgba(0,0,0,0.6)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg style={{ width: '16px', height: '16px', color: 'white', marginLeft: '2px' }} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Fullscreen carousel for images and videos */}
      {carouselIndex !== null && (() => {
        const current = allMedia[carouselIndex];
        return (
        <div
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-50"
          onClick={() => setCarouselIndex(null)}
          onTouchStart={(e) => { touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
          onTouchEnd={(e) => {
            if (!touchStartRef.current) return;
            const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
            const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
            touchStartRef.current = null;
            if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;
            if (dx < 0 && carouselIndex < allMedia.length - 1) {
              setCarouselIndex(carouselIndex + 1);
            } else if (dx > 0 && carouselIndex > 0) {
              setCarouselIndex(carouselIndex - 1);
            }
          }}
        >
          <button className="absolute top-4 right-4 text-white hover:text-gray-400 z-10" onClick={() => setCarouselIndex(null)}>
            <X className="w-8 h-8" />
          </button>
          {carouselIndex > 0 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/50 rounded-full p-3 hover:bg-black/70 z-10 hidden sm:block"
              onClick={(e) => { e.stopPropagation(); setCarouselIndex(carouselIndex - 1); }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
          )}
          {carouselIndex < allMedia.length - 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/50 rounded-full p-3 hover:bg-black/70 z-10 hidden sm:block"
              onClick={(e) => { e.stopPropagation(); setCarouselIndex(carouselIndex + 1); }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          )}
          {current.type === 'image' ? (
            <img src={current.src} alt="" className="max-w-full max-h-[85vh] object-contain" onClick={(e) => e.stopPropagation()} />
          ) : (
            <video
              key={current.src}
              src={current.src}
              className="max-w-full max-h-[85vh]"
              controls
              autoPlay
              muted
              playsInline
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {allMedia.length > 1 && (
            <div className="absolute bottom-6 flex items-center space-x-2">
              {allMedia.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full ${i === carouselIndex ? 'bg-white' : 'bg-gray-500'}`} />
              ))}
            </div>
          )}
          <div className="absolute bottom-12 text-white text-sm">{carouselIndex + 1} / {allMedia.length}</div>
        </div>
        );
      })()}
    </div>
  );
};

export default PostMediaGrid;
